import axios from 'axios';

import type {
  ActivePreset,
  BeVirtualPreset,
  GetPresetResponse,
  GetPresetsResponse,
  PostPresetRequest,
  PostPresetResponse,
  Preset,
  PutPresetRenameRequest,
  PutPresetRenameResponse,
  PutPresetRequest,
  PutPresetResponse,
  VirtualPreset,
} from '../types/api/preset';
import {stripLocalGridSettings} from '../utils/stripLocalGridSettings';
import {AXIOS_BASE_CONFIG, makeInstanceConfig} from './axiosConfig';
import {handleCopyError} from './handleCopyError';
import type {ApiContext} from './types';

export function createPresetApi(ctx: ApiContext) {
  const BASE = `/v6/data-grid/${ctx.gridCode}`;
  const instanceConfig = makeInstanceConfig(ctx.presetInstanceId);
  const freshInstanceConfig = {...instanceConfig, cache: false};

  const getPreset = (presetId: string, options?: {fresh?: boolean; forCopy?: boolean}) =>
    axios
      .get<GetPresetResponse>(
        `${BASE}/preset/${presetId}`,
        options?.fresh ? freshInstanceConfig : instanceConfig
      )
      .then((response) => response.data)
      .catch((error: unknown) =>
        options?.forCopy ? handleCopyError(ctx, error) : ctx.handleApiError(error)
      );

  const getVirtualPreset = (options?: {fresh?: boolean}) =>
    axios
      .get<BeVirtualPreset>(
        `${BASE}/virtual-preset`,
        options?.fresh ? freshInstanceConfig : instanceConfig
      )
      .then((response) => response.data)
      .catch(ctx.handleApiError);

  const getActivePreset = (
    virtualPreset: VirtualPreset,
    customPresets: Preset[]
  ): Promise<ActivePreset | void> => {
    const activeCustomPreset = customPresets.find((customPreset) => customPreset.isActive);

    if (!activeCustomPreset) {
      return Promise.resolve(virtualPreset);
    }

    return getPreset(activeCustomPreset.id);
  };

  return {
    getActivePreset,
    getPreset,
    getVirtualPreset,
    deleteVirtualPreset: () =>
      axios
        .delete(`${BASE}/virtual-preset`, AXIOS_BASE_CONFIG)
        .then(() => getVirtualPreset())
        .catch(ctx.handleApiError),
    // CRUD mutations use handleMutationError: a failure REJECTS (surfaced locally
    // as a per-pill error) instead of blanking the whole grid via the error context.
    createPreset: (presetRequest: PostPresetRequest) =>
      axios
        .post<PostPresetResponse>(
          `${BASE}/preset`,
          {
            ...presetRequest,
            ...(presetRequest.gridSettings
              ? {gridSettings: stripLocalGridSettings(presetRequest.gridSettings)}
              : {}),
            instance: ctx.presetInstanceId ?? null,
          },
          AXIOS_BASE_CONFIG
        )
        .then((response) => response.data)
        .catch(ctx.handleMutationError),
    updatePreset: (presetId: string, presetRequest: PutPresetRequest) =>
      axios
        .put<PutPresetResponse>(
          `${BASE}/preset/${presetId}`,
          {
            ...presetRequest,
            ...(presetRequest.gridSettings
              ? {gridSettings: stripLocalGridSettings(presetRequest.gridSettings)}
              : {}),
            instance: ctx.presetInstanceId ?? null,
          },
          AXIOS_BASE_CONFIG
        )
        .then((response) => response.data)
        .catch(ctx.handleMutationError),
    updateRenamePreset: (presetId: string, presetRequest: PutPresetRenameRequest) =>
      axios
        .put<PutPresetRenameResponse>(
          `${BASE}/preset/${presetId}/rename`,
          presetRequest,
          instanceConfig
        )
        .then((response) => response.data)
        .catch(ctx.handleMutationError),
    deletePreset: (presetId: string) =>
      axios.delete(`${BASE}/preset/${presetId}`, AXIOS_BASE_CONFIG).catch(ctx.handleMutationError),
    presetOrder: (presetsOrder: string[]) =>
      axios
        .put(`${BASE}/preset/order`, {presetsOrder}, instanceConfig)
        .catch(ctx.handleMutationError),
    activatePreset: (activePresetId: string | null) =>
      axios
        .put(`${BASE}/preset/active`, {activePreset: activePresetId}, instanceConfig)
        .catch(ctx.handleApiError),
    activatePresetForCopy: () =>
      axios
        .put(`${BASE}/preset/active`, {activePreset: null}, instanceConfig)
        .catch(ctx.handleMutationError),
    getPresets: () =>
      axios
        .get<GetPresetsResponse>(`${BASE}/preset`, instanceConfig)
        .then((response) => response.data)
        .catch(ctx.handleApiError),
    updateVirtualPreset: (virtualPreset: VirtualPreset, options?: {forCopy?: boolean}) =>
      axios
        .put(
          `${BASE}/virtual-preset`,
          {
            ...virtualPreset,
            ...(virtualPreset.gridSettings
              ? {gridSettings: stripLocalGridSettings(virtualPreset.gridSettings)}
              : {}),
          },
          instanceConfig
        )
        .catch(options?.forCopy ? ctx.handleMutationError : ctx.handleApiError),
  };
}
